<template>
  <div>
    <div class="d-flex flex-column align-center justify-center pt-3">
      <div>
        <v-radio-group
          v-model="shippingMode"
          row
          dense
          hide-details
          class="mt-0 d-flex flex-column"
          @change="selectShipping"
        >
          <v-radio
            v-for="(option, index) in shippingOptions"
            :key="option.value"
            :label="option.text"
            :value="option.value"
            :class="{'mr-0': index === shippingOptions.length - 1}"
            active-class="selected-option"
            class="radio-input"
          />
        </v-radio-group>
      </div>
      <transition-group
        enter-active-class="fadeIn"
        leave-active-class="fadeOutFast"
        mode="out-in"
      >
        <v-form v-if="deliverySelected" :key="'deliveryData'" v-model="validForm" class="mt-3">
          <div class="mb-2 d-flex flex-column flex-md-row align-center">
            <v-subheader class="mr-md-3 field-label d-flex justify-center justify-md-end text-center text-md-end">
              {{ districtQuestion.text }}
            </v-subheader>
            <div>
              <div v-if="!isMobile" style="height: 22px;"></div>
              <v-autocomplete
                v-model="district"
                :items="cityDistricts"
                :attach="true"
                :menu-props="{
                  allowOverflow: true,
                  top: true,
                  nudgeBottom: 100
                }"
                dense
                filled
                rounded
                solo
                flat
                class="dropdown-input"
                placeholder="-- Selectati --"
                @change="updateEvent"
              />
            </div>
          </div>
          <div class="mb-2 d-flex flex-column flex-md-row align-center">
            <v-subheader class="mr-md-3 field-label d-flex justify-center justify-md-end text-center text-md-end">
              {{ addressQuestion.text }}
            </v-subheader>
            <div>
              <div v-if="!isMobile" style="height: 22px;"></div>
              <v-text-field
                v-model="address"
                :rules="addressQuestion.rules"
                dense
                filled
                rounded
                solo
                flat
                class="text-input"
                @input="updateEvent"
              />
            </div>
          </div>
          <div class="mb-2 d-flex flex-column flex-md-row align-center">
            <v-subheader class="mr-md-3 field-label d-flex justify-center justify-md-end text-center text-md-end">
              {{ shippingDateQuestion.text }}
            </v-subheader>
            <v-menu
              ref="dateMenu"
              v-model="dateMenu"
              :attach="true"
              :close-on-content-click="false"
              :allow-overflow="true"
              :nudge-right="isMobile ? -24 : 12"
              content-class="custom-date-picker"
              top
              transition="scroll-y-reverse-transition"
              max-width="355"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <div>
                  <div v-if="!isMobile" style="height: 22px;"></div>
                  <v-text-field
                    v-model="computedDateFormatted"
                    v-bind="attrs"
                    dense
                    filled
                    rounded
                    solo
                    flat
                    readonly
                    hint="DD/MM/YYYY"
                    class="text-input"
                    prepend-inner-icon="mdi-calendar"
                    v-on="on"
                  />
                </div>
              </template>
              <v-date-picker
                v-model="shippingDate"
                :allowed-dates="allowedDates"
                :min="minDate"
                :max="maxDate"
                no-title
                locale="ro"
                first-day-of-week="1"
                @input="selectDate"
              >
                <div class="d-flex justify-space-between" style="width: 100%">
                  <v-btn
                    :disabled="!canShipToday"
                    color="#df0100"
                    class="base-button mr-2"
                    depressed
                    tile
                    dark
                    width="125"
                    @click="setDate('today')"
                  >
                    Azi
                  </v-btn>
                  <v-btn
                    :disabled="!canShipTomorrow"
                    color="#df0100"
                    class="base-button"
                    depressed
                    tile
                    dark
                    width="125"
                    @click="setDate('tomorrow')"
                  >
                    Maine
                  </v-btn>
                </div>
              </v-date-picker>
            </v-menu>
          </div>
          <div class="mb-2 d-flex flex-column flex-md-row align-center">
            <v-subheader class="mr-md-3 field-label d-flex justify-center justify-md-end text-center text-md-end">
              {{ notesQuestion.text }}
            </v-subheader>
            <div>
              <div v-if="!isMobile" style="height: 22px;"></div>
              <v-textarea
                v-model="notes"
                dense
                filled
                rounded
                solo
                flat
                class="text-input"
                rows="2"
                @input="updateEvent"
              />
            </div>
          </div>
        </v-form>
        <div v-if="selfPickSelected" :key="'selfPickInfo'" class="mt-3">
          <company-info-card />
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import { cityDistricts, confirmationQuestions, shippingOptions } from '@/_utils/static-data';
import CompanyInfoCard from '@/components/rca/CompanyInfoCard';
export default {
  name: 'ShippingForm',
  components: { CompanyInfoCard },
  data() {
    return {
      shippingMode: null,
      district: null,
      address: null,
      shippingDate: null,
      notes: null,
      dateMenu: false,
      minDate: new Date().toISOString().substr(0, 10),
      validForm: false
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.width < 961;
    },
    shippingOptions() {
      return shippingOptions;
    },
    deliverySelected() {
      return this.shippingMode === 'delivery';
    },
    selfPickSelected() {
      return this.shippingMode === 'self';
    },
    maxDate() {
      const today = new Date();
      today.setMonth(today.getMonth() + 1);
      return today.toISOString().substr(0, 10);
    },
    computedDateFormatted() {
      return this.formatDate(this.shippingDate);
    },
    isValid() {
      if (this.selfPickSelected) { return true; }
      else if (this.deliverySelected) {
        return !!(this.validForm && !!this.district && !!this.shippingDate);
      }
      return false;
    },
    cityDistricts() {
      return cityDistricts;
    },
    canShipToday() {
      const todayIndex = new Date().getDay();
      return todayIndex !== 0 && todayIndex !== 6;
    },
    canShipTomorrow() {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowIndex = tomorrow.getDay();
      return tomorrowIndex !== 0 && tomorrowIndex !== 6;
    },
    districtQuestion() {
      return this.getQuestionById(7);
    },
    addressQuestion() {
      return this.getQuestionById(8);
    },
    shippingDateQuestion() {
      return this.getQuestionById(9);
    },
    notesQuestion() {
      return this.getQuestionById(10);
    }
  },
  methods: {
    setDate(option) {
      const today = new Date();
      if (option === 'today') {
        this.shippingDate = today.toISOString().substr(0, 10);
      } else if (option === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        this.shippingDate = tomorrow.toISOString().substr(0, 10);
      }
      this.selectDate();
    },
    allowedDates(val) {
      const dayNumber = new Date(val).getDay();
      return dayNumber !== 0 && dayNumber !== 6;
    },
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },
    getQuestionById(id) {
      return confirmationQuestions(id)?.[0];
    },
    selectDate() {
      this.updateEvent();
      this.dateMenu = false;
    },
    updateEvent() {
      const deliveryData = [
        {
          questionText: 'Modalitate de livrare',
          text: this.shippingMode
        }
      ];
      if (this.deliverySelected) {
        deliveryData.push({
          questionText: this.districtQuestion.text,
          text: this.district,
          value: 'district'
        });
        deliveryData.push({
          questionText: this.addressQuestion.text,
          text: this.address,
          value: 'address'
        });
        deliveryData.push({
          questionText: this.shippingDateQuestion.text,
          text: this.computedDateFormatted,
          value: 'shippingDate'
        });
        deliveryData.push({
          questionText: this.notesQuestion.text,
          text: this.notes,
          value: 'notes'
        });
      }
      setTimeout(() => {
        this.$emit('updateData', {
          data: deliveryData,
          isValid: this.isValid
        });
      }, 400);
    },
    selectShipping() {
      this.updateEvent();
    }
  }
};
</script>

<style scoped>
.text-input, .dropdown-input {
  border-radius: 4px;
  width: 355px;
}
.text-input ::v-deep input, .dropdown-input ::v-deep .v-select__slot input {
  border: none;
}
.field-label {
  width: 180px;
}
.radio-input ::v-deep label {
  margin-bottom: 0 !important;
  padding: 4px 0;
}
.radio-input {
  padding: 2px 12px;
  min-width: 157px !important;
  border-radius: 4px;
  border: 1px solid #df0100;
}
.radio-input ::v-deep .v-input--selection-controls__input {
  display: none;
}
.selected-option {
  background-color: #df0100;
}
.selected-option ::v-deep label {
  color: #FFFFFF !important;
}
@media screen and (max-width: 600px) {
  .field-label {
    width: 100%;
  }
  .radio-input {
    min-width: 120px !important;
  }
  .text-input, .dropdown-input {
    width: 100%;
    min-width: 260px;
  }
}
.custom-date-picker ::v-deep table td, .custom-date-picker ::v-deep table th {
  padding: 0;
  border: none;
}
.custom-date-picker ::v-deep button {
  padding: 0;
}
.custom-date-picker ::v-deep button:hover {
  border-color: transparent;
  background: transparent;
}
</style>
